import { createFilledElement, fillTemplate } from './fill-template.js';
import { updatePagination } from './pagination.js';

const datePickerTagName = 'date-picker'; // See date-picker.js

/**
 * Filter template selectors
 */
export const selectors = Object.freeze({
    // not in a template
    dialog: '.filter__form-dialog',
    form: '.filter__form',
    mobileShow: '.filter__mobile-show-dialog-button',
    desktopSortSelect: 'select[name="desktop-sort"]',
    resultsContentContainer: '.filter__results-content-container',
    // in multiple templates
    chipsContainers: '.filter__chips-container',
    resets: '.filter__form-reset',
    // mobileFormHeader
    mobileClose: '.filter__mobile-form-header-close',
    mobileHeaderBottom: '.filter__mobile-form-header-bottom',
    // textInput
    searchField: '.filter__search-field',
    // filterFacet
    details: '.filter__details',
    detailsSummary: '.filter__details > summary',
    detailsContent: '.filter__details-content',
    facetSearches: 'input[name="facet-filter"]',
    checkboxList: '.filter__checkbox-list',
    // filterFacetItem
    checkboxes: 'input[type="checkbox"]',
    tooltips: '.filter__tooltip',
    // mobileFormFooter
    mobileSubmit: '.filter__mobile-form-submit',
    // resultsViewOptions
    resultsViewToggles: 'input[name="view"]',
    // desktopChipsContainer
    chipTemplate: '.filter__chip-template',
    // resultsFooter
    paginationForm: '.filter__pagination-form',
});

/**
 * Given an array of form controls, find the matching select or input element and populate its value.
 * @param {Element[]} formControls
 * @param {string} name
 * @param {string} value
 * @param {boolean} check
 * @returns {Element?}
 */
export const fillInput = (formControls, name, value, check) => {
    let formControl = null;
    for (formControl of formControls) { // eslint-disable-line no-restricted-syntax
        if (name === formControl.name) {
            if (formControl.tagName === 'SELECT') {
                formControl.value = value;
                break;
            } if (formControl.tagName === 'INPUT') {
                if (['checkbox', 'radio'].includes(formControl.type)) {
                    if (value === formControl.value) {
                        formControl.checked = check;
                        break;
                    }
                }
                if (['text', 'search'].includes(formControl.type)) {
                    formControl.value = value;
                    break;
                }
                if (formControl.type === 'date') {
                    const datePicker = formControl.closest(datePickerTagName);
                    if (datePicker) {
                        datePicker.clearSelection();
                    } else {
                        formControl.value = value;
                    }
                    break;
                }
            }
        }
    }
    return formControl;
};

const slideUp = (element, duration = 300) => {
    return new Promise((resolve) => {
        element.style.height = element.offsetHeight + 'px';
        element.style.transitionProperty = 'height, margin, padding';
        element.style.transitionDuration = duration + 'ms';
        element.offsetHeight; // trigger reflow
        element.style.overflow = 'hidden';
        element.style.height = 0;
        element.style.paddingTop = 0;
        element.style.paddingBottom = 0;
        element.style.marginTop = 0;
        element.style.marginBottom = 0;
        setTimeout(() => {
            element.style.display = 'none';
            element.style.removeProperty('height');
            element.style.removeProperty('padding-top');
            element.style.removeProperty('padding-bottom');
            element.style.removeProperty('margin-top');
            element.style.removeProperty('margin-bottom');
            element.style.removeProperty('overflow');
            element.style.removeProperty('transition-duration');
            element.style.removeProperty('transition-property');
            resolve();
        }, duration);
    });
};

const slideDown = (element, duration = 300) => {
    return new Promise((resolve) => {
        element.style.removeProperty('display');
        let display = window.getComputedStyle(element).display;
        if (display === 'none') display = 'block';
        element.style.display = display;
        let height = element.offsetHeight;
        element.style.overflow = 'hidden';
        element.style.height = 0;
        element.style.paddingTop = 0;
        element.style.paddingBottom = 0;
        element.style.marginTop = 0;
        element.style.marginBottom = 0;
        element.offsetHeight; // trigger reflow
        element.style.transitionProperty = 'height, margin, padding';
        element.style.transitionDuration = duration + 'ms';
        element.style.height = height + 'px';
        element.style.removeProperty('padding-top');
        element.style.removeProperty('padding-bottom');
        element.style.removeProperty('margin-top');
        element.style.removeProperty('margin-bottom');
        setTimeout(() => {
            element.style.removeProperty('height');
            element.style.removeProperty('overflow');
            element.style.removeProperty('transition-duration');
            element.style.removeProperty('transition-property');
            resolve();
        }, duration);
    });
};

/**
 * Adds slide up/down animation when details element is opened/closed
 * @param {MouseEvent} ev
 */
const handleSummaryClick = (ev) => {
    ev.preventDefault();
    const detailsEl = ev.currentTarget.closest('details');
    const contentEl = detailsEl.querySelector('div');

    if (!detailsEl.open) {
        // Open
        contentEl.style.display = 'none';
        detailsEl.open = true;
        slideDown(contentEl);
    } else {
        // Close
        slideUp(contentEl).then(() => {
            detailsEl.open = false;
        });
    }
};

/**
 * Adds filter facet related listeners
 * @param {HTMLElement} parentElement
 */
export const addFacetListeners = (parentElement) => {

    // Adds listeners necessary to animate filter facet expand/collapse
    parentElement.querySelectorAll(selectors.detailsSummary).forEach(
        (summary) => summary.addEventListener('click', handleSummaryClick)
    );

    // Reorder function for this list
    const reorderSelectedToTop = (list) => {
        const items = Array.from(list.children);
        items.sort((a, b) => {
            const aChecked = a.querySelector('input[type="checkbox"]')?.checked;
            const bChecked = b.querySelector('input[type="checkbox"]')?.checked;

            // Selected first
            if (aChecked && !bChecked) return -1;
            if (!aChecked && bChecked) return 1;

            // If both are the same (selected or unselected), preserve original order
            return Number(a.dataset.originalIndex) - Number(b.dataset.originalIndex);
        });

        // Move elements in new order
        items.forEach((item) => list.appendChild(item));
    };

    const resetOrder = (list) => {
        const items = Array.from(list.children);
        items.sort((a, b) => Number(a.dataset.originalIndex) - Number(b.dataset.originalIndex));
        items.forEach((item) => list.appendChild(item));
    };

    // Select all filter checkbox lists on the page
    const allCheckboxLists = parentElement.querySelectorAll(selectors.checkboxList);

    allCheckboxLists.forEach((checkboxList) => {
        // Add event listener on checkbox change for this list
        checkboxList.addEventListener('change', (event) => {
            if (event.target.type === 'checkbox') {
                reorderSelectedToTop(checkboxList);
            }
        });
    });

    const resetButtons = parentElement.querySelectorAll(selectors.resets);
    resetButtons.forEach((button) => {
        button.addEventListener('click', () => {
            allCheckboxLists.forEach((list) => resetOrder(list));
        });
    });
};

/** @param {HTMLDialogElement} filterDialog */
export const showMobileFilter = (filterDialog) => {
    document.body.classList.add('no-scroll');
    filterDialog.style.left = '100%';
    filterDialog.showModal();
    filterDialog.style.left = null;
};

/** @param {HTMLDialogElement} filterDialog */
export const closeMobileFilter = (filterDialog) => {
    document.body.classList.remove('no-scroll');
    window.setTimeout(() => {
        filterDialog.close();
        filterDialog.style.left = null;
    }, 500); // Timeout matches the .filter__form-dialog transition duration
    // Cannot use transitionend because the Crownpeak autofix interferes with it
    filterDialog.style.left = '100%';
};

/**
 * Trigger a callback function when a form's select or non-textual input changes.
 * @param {HTMLFormElement} formElement
 * @param {Function} callback
 */
export const onFormControlChange = (formElement, callback) => {
    [...formElement.elements].forEach((formControl) => {
        if (['SELECT', 'INPUT'].includes(formControl.tagName) && !['text', 'search'].includes(formControl.type)) {
            formControl.addEventListener('change', () => callback());
        }
    });
};

/** @param {Event} ev */
export const adjustTooltipPosition = (ev) => {
    const tooltip = ev.currentTarget.querySelector('.filter__tooltip-content');

    // Ensure that tooltip does not overflow the right viewport
    if (tooltip.getBoundingClientRect().left < 0) {
        tooltip.style.transform = '';
    }
    const rightEdgeDistance = document.documentElement.clientWidth - tooltip.getBoundingClientRect().right - 24;
    if (rightEdgeDistance < 0) {
        tooltip.style.transform = `translateX(${rightEdgeDistance}px)`;
    }
};

/** @param {MouseEvent} ev */
const handleTooltipClick = (ev) => {
    ev.preventDefault(); // Prevent click-through on tooltips
    ev.stopPropagation();
};

/** @param {HTMLElement} tooltip */
export const handleTooltip = (tooltip) => {
    tooltip.addEventListener('click', handleTooltipClick);
    tooltip.addEventListener('focus', adjustTooltipPosition);
    tooltip.addEventListener('mouseenter', adjustTooltipPosition);
};

const searchFilterAttribute = 'data-filtered';

/**
 * @param {HTMLInputElement} checkbox - must match the structure of filter-templates.filterFacetItem
 * @param {number} count
 * @param {boolean} alwaysShow - if true, the item is not hidden even if the count is zero
 */
export const updateFacetItemCount = (checkbox, count, alwaysShow = false) => {
    if (!checkbox) {
        return;
    }

    const listItem = checkbox.closest('li');
    const span = listItem.querySelector('span');
    span.dataset.count = count;

    // check if this item was filtered by the facet search
    const isFiltered = listItem.getAttribute(searchFilterAttribute) === 'true';
    listItem.setAttribute('aria-hidden', (count < 1 && !checkbox.checked && !alwaysShow) || isFiltered);
};

/** @param {InputEvent} ev */
export const handleFacetSearch = (ev) => {
    const input = ev.currentTarget;
    const inputValue = input.value?.toLowerCase();

    const listItems = input.closest('.filter__details-content').querySelectorAll('li');
    listItems.forEach((listItem) => {
        const isMatch = listItem.dataset.label.toLowerCase().includes(inputValue);
        const noResults = listItem.querySelector('span').dataset.count < 1;
        listItem.setAttribute(searchFilterAttribute, !isMatch);
        listItem.setAttribute('aria-hidden', !isMatch || noResults);
    });
};

/** @param {NodeListOf<Element> | Element[]} filterDetails */
export const showFilterSearchFields = (filterDetails) => {
    filterDetails.forEach((facet) => {
        // Assign original index to li items
        const listItems = facet.querySelectorAll('li');
        listItems.forEach((li, index) => {
            if (!li.hasAttribute('data-original-index')) {
                li.dataset.originalIndex = index;
            }
        });

        const searchField = facet.querySelector('.filter__search-field');
        if (searchField) {
            const numOptions = [...facet.querySelectorAll('li:not([aria-hidden="true"])')].length;
            const hasDatePicker = !!facet.querySelector(datePickerTagName);
            if (numOptions > 12 || hasDatePicker) {
                searchField.hidden = false;
            } else {
                searchField.hidden = true;
            }
        }
    });
};

/** @param {NodeListOf<Element> | Element[]} filterDetails */
export const hideFacetsWithNoResults = (filterDetails) => {
    filterDetails.forEach((filterFacet) => filterFacet.setAttribute(
        'aria-hidden',
        // Facets with results and date pickers are shown
        filterFacet.querySelectorAll('input[type="date"], li:not([aria-hidden="true"])').length < 1
    ));
};

/**
 * @param {HTMLElement} parentElement
 * @param {HTMLFormElement} paginationForm
 * @param {Record<string, number>} data
 */
export const updateResultCounts = (
    parentElement,
    paginationForm,
    {
        firstResultNum,
        lastResultNum,
        totalResults,
        currentPageNum,
        pageCount
    }
) => {
    fillTemplate(parentElement, 'data-x', { firstResultNum, lastResultNum, totalResults }, { keepIfEmpty: true });
    updatePagination(paginationForm, { currentPageNum, pageCount: pageCount || 1 });
};

/**
 * @param {MouseEvent} ev
 * @param {HTMLFormElement} form
 * @param {Function} callback
 */
export const handleChipClick = (ev, form, callback) => {
    ev.preventDefault();
    const { name, value } = ev.currentTarget.dataset;
    fillInput([...form.elements], name, value, false);
    callback();
};

/**
 * @param {HTMLFormElement} filterForm
 * @param {HTMLTemplateElement} chipTemplate
 * @param {Function} chipCallback
 * @param {NodeListOf<Element> | Element[]} chipContainers
 * @param {string[]} otherChipParams
 * @returns {boolean} True if the number of chips is nonzero.
 */
export const updateChips = (filterForm, chipTemplate, chipCallback, chipContainers, otherChipParams = []) => {
    const handledDatePickers = new Set();
    const chipData = [...filterForm.elements]
        .map((formInput) => {
            // Get data from checked checkboxes
            if (formInput.type === 'checkbox' && formInput.checked) {
                return {
                    label: formInput.closest('li').dataset.label,
                    name: formInput.name,
                    value: formInput.value,
                };
            }
            // Get data from date pickers
            if (formInput.type === 'date' && formInput.value !== '') {
                const datePicker = formInput.closest(datePickerTagName);
                if (datePicker && !handledDatePickers.has(datePicker)) {
                    handledDatePickers.add(datePicker);
                    return {
                        label: datePicker.getLabel(),
                        name: datePicker.getName(),
                        value: ''
                    };
                }
            }
            return null;
        })
        .filter((data) => data != null);

    // Get data for other specified params
    const formData = new FormData(filterForm);
    const otherChipData = otherChipParams
        .map((param) => ({ label: formData.get(param)?.toString(), name: param, value: '' }))
        .filter(({ label }) => label != null && label !== '');
    chipData.unshift(...otherChipData);

    const hasChips = chipData.length > 0;

    // Create chips and fill them into chip containers
    chipContainers.forEach((chipContainer) => {
        const chips = chipData.map((data) => {
            const chip = createFilledElement(chipTemplate, 'data-xt', data, { keepIfEmpty: true });
            chip.querySelector('button').addEventListener('click', (ev) => handleChipClick(ev, filterForm, chipCallback));
            return chip;
        });
        chipContainer.replaceChildren(...chips);
        chipContainer.hidden = !hasChips;
    });

    return hasChips;
};

/**
 * @param {string} dateStringA
 * @param {string} dateStringB
 * @returns {number}
 */
export const compareDates = (dateStringA, dateStringB) => {
    let dateA;
    let dateB;
    try {
        dateA = new Date(dateStringA.replace(' ', 'T'));
    } catch {
        dateA = null;
    }
    try {
        dateB = new Date(dateStringB.replace(' ', 'T'));
    } catch {
        dateB = null;
    }
    return dateA - dateB;
};

/** @param {HTMLElement} parentElement */
export const addViewToggleListeners = (parentElement) => {
    const resultsContentContainer = parentElement.querySelector(selectors.resultsContentContainer);

    /** @param {Event} ev */
    const handleViewChange = (ev) => {
        resultsContentContainer.dataset.view = ev.currentTarget.value;
    };

    parentElement.querySelectorAll(selectors.resultsViewToggles).forEach(
        (el) => el.addEventListener('change', handleViewChange)
    );
};
